import { NumberFormatter, Paper, Badge, Stack, Table, TableData, Text, Group } from '@mantine/core'
import { OfferFormDecisionBadge } from '@shared/components/offers/offer-form-decision-badge/offer-form-decision-badge'
import { OfferFormDecisionInput } from '@shared/components/offers/offer-form-decision-input/offer-form-decision-input'
import { WoolLotOffer } from '@shared/components/offers/offer-types'

type OfferFormLotCardProps = {
  lot: WoolLotOffer
  index: number
  readOnly: boolean
}

export function OfferFormLotCard({ lot, index, readOnly }: OfferFormLotCardProps) {
  const tableData: TableData = {
    head: ['Desc', 'Bales', 'Mic', 'VM', 'Yld', 'Col', 'SL', 'SS', 'Line', 'Gsy Kg', 'Ref'],
    body: [
      [
        lot.mainBaleHeaderPurchaseDescription,
        lot.totalBales,
        <NumberFormatter
          value={lot.coreTestMicron || undefined}
          thousandSeparator
          decimalScale={1}
        />,
        <NumberFormatter
          value={lot.coreTestVegetableMatterBase || undefined}
          thousandSeparator
          decimalScale={1}
        />,
        <NumberFormatter
          value={lot.coreTestYield || undefined}
          thousandSeparator
          decimalScale={1}
        />,
        <NumberFormatter
          value={lot.coreTestColourYZ || undefined}
          thousandSeparator
          decimalScale={1}
        />,
        <NumberFormatter
          value={lot.coreTestStapleLengthGreasy || undefined}
          thousandSeparator
          decimalScale={0}
        />,
        <NumberFormatter
          value={lot.coreTestStapleStrength || undefined}
          thousandSeparator
          decimalScale={0}
        />,
        lot.lineNumber,
        <NumberFormatter value={lot.netKg || undefined} thousandSeparator />,
        <>
          <Text size='sm'>{lot.internalReferenceCode}</Text>
          {lot.interlotWarning && (
            <Badge color='gray' variant='light' c='black' fw={600} aria-label='Interlot Part'>
              IP
            </Badge>
          )}
        </>,
      ],
    ],
  }

  return (
    <Paper
      p='md'
      withBorder
      bd='1px solid gray.2'
      bg='gray.0'
      aria-label={`Offer Line ${index + 1}`}
    >
      <Stack>
        <Table.ScrollContainer minWidth='md'>
          <Table withTableBorder withColumnBorders bg='white' data={tableData} />
        </Table.ScrollContainer>
        <Group justify='space-between'>
          <Text size='lg'>
            Offer Price:{' '}
            <Text span fw={700}>
              {!!lot.cleanOfferPrice && (
                <NumberFormatter
                  value={lot.cleanOfferPrice ?? ''}
                  suffix=' cln / '
                  thousandSeparator
                  decimalScale={0}
                />
              )}
              {!!lot.greasyOfferPrice && (
                <NumberFormatter
                  value={lot.greasyOfferPrice ?? ''}
                  suffix=' gsy'
                  thousandSeparator
                  decimalScale={0}
                />
              )}
            </Text>
          </Text>

          {/* If read only, show a badge of decision */}
          {readOnly && lot.status && <OfferFormDecisionBadge status={lot.status} index={index} />}
          {/* If not read only, show inputs for decision */}
          {!readOnly && lot.id && <OfferFormDecisionInput woolLotId={lot.id} index={index} />}
        </Group>
        <Text>
          <Text span>
            <Text span>The current market is approx </Text>
            {!!lot.cleanMarketPrice && (
              <NumberFormatter
                value={lot.cleanMarketPrice ?? ''}
                suffix=' cln c/kg'
                thousandSeparator
                decimalScale={0}
              />
            )}
            {!!lot.cleanMarketPrice && !!lot.greasyMarketPrice && <Text span> and </Text>}
            {!!lot.greasyMarketPrice && (
              <NumberFormatter
                value={lot.greasyMarketPrice ?? ''}
                suffix=' gsy c/kg'
                thousandSeparator
                decimalScale={0}
              />
            )}
            <Text span>.</Text>
          </Text>
          {lot.displayValueAdd && (
            <Text span>
              {' '}
              If accepted, the value added to this line compared to the current market will be
              approximately{' '}
              <NumberFormatter
                prefix='$'
                value={lot.valueAdd || undefined}
                thousandSeparator
                decimalScale={0}
              />
              .
            </Text>
          )}
          {lot.brandPartner && (
            <Text span>
              {' '}
              This line is destined for a{' '}
              <Text span fw={700}>
                {lot.brandPartner}
              </Text>{' '}
              order.
            </Text>
          )}
        </Text>
      </Stack>
    </Paper>
  )
}
